import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Report from "../../components/Report";
import { CvpColumn, CvpTable } from "../Table/CvpTable";
import { useAuth } from "../providers/AuthProvider";
import {
  PaymentStatusFilterState,
  PaymentStatusFilterToolbar,
  SupplierOption,
} from "./PaymentStatusFilterToolbar";
import {
  fetchOnBoardVendorDetails,
  fetchOnBoardVendors,
} from "./VendorService";

interface InvoicePaymentStatusProps {}

const API_PAGE_SIZE = 1000;
const MAX_CACHED_API_PAGES = 5;
const MAX_SUBVENDOR_PAYLOAD_LIMIT = 10;

export interface PaymentData {
  invoiceNumber: string;
  vendorNumber: string;
  paymentMethod: string;
  dueDate: string;
  checkDate: string;
  remainingDays: number;
  amountDue: string;
  paymentStatus: string;
  paymentDate: string;
  referenceDate: string;
  paymentReferenceNumber: string;
  grossAmount: string;
  discountPercent: string;
  adjustment: string;
  netAmount: string;
  voidDate: string;
  routingStatus: string;
  invoiceCurrency: string;
}

export interface Pagination {
  hasMore: boolean;
  nextPage: boolean;
  page: number;
  pageSize: number;
  recordsReturned: number;
  totalRecords: number;
}

interface PaymentResponse {
  data: PaymentData[];
  pagination: Pagination;
}

export const formatDateDisplay = (dateString?: string): string => {
  if (!dateString) return "";
  const dateOnly = dateString.split("T")[0];
  const parts = dateOnly.split("-");
  if (parts.length !== 3) return dateString;

  const [yearStr, monthStr, dayStr] = parts;
  const monthIndex = parseInt(monthStr, 10) - 1;
  const day = parseInt(dayStr, 10);

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  if (monthIndex < 0 || monthIndex > 11 || isNaN(day)) {
    return dateString;
  }
  return `${months[monthIndex]} ${day}, ${yearStr}`;
};

export const formatCurrencyDisplay = (
  amount?: string | number,
  currencyCode?: string,
): string => {
  if (amount === undefined || amount === null || amount === "") return "";
  const numericVal = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(numericVal)) return String(amount);

  const formattedNumber = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format(numericVal);

  const trimmedCurrency = currencyCode?.trim();
  return trimmedCurrency
    ? `$${formattedNumber} ${trimmedCurrency}`
    : `$${formattedNumber}`;
};

export const formatDiscountDisplay = (val?: string | number): string => {
  if (val === undefined || val === null || val === "") return "";
  const numericVal = typeof val === "string" ? parseFloat(val) : val;
  if (isNaN(numericVal)) return String(val);

  const percentage = numericVal * 100;
  const roundedUp = (Math.ceil(percentage * 100) / 100).toFixed(2);

  return `${roundedUp}%`;
};

export const invoiceColumns: CvpColumn<PaymentData>[] = [
  { key: "invoiceNumber", header: "Invoice" },
  { key: "vendorNumber", header: "Vendor Number" },
  { key: "vendorName", header: "Supplier" },
  { key: "paymentMethod", header: "Payment Method" },
  {
    key: "dueDate",
    header: "Scheduled Payment Date",
    render: (row) => formatDateDisplay(row.dueDate),
  },
  {
    key: "checkDate",
    header: "Settlement Date",
    render: (row) => formatDateDisplay(row.checkDate),
  },
  { key: "remainingDays", header: "Remaining Days" },
  {
    key: "amountDue",
    header: "Amount Due",
    render: (row) => formatCurrencyDisplay(row.amountDue, row.invoiceCurrency),
  },
  { key: "paymentStatus", header: "Status" },
  {
    key: "paymentDate",
    header: "Payment Date",
    render: (row) => formatDateDisplay(row.paymentDate),
  },
  { key: "paymentReferenceNumber", header: "Reference Number" },
  {
    key: "grossAmount",
    header: "Gross",
    render: (row) =>
      formatCurrencyDisplay(row.grossAmount, row.invoiceCurrency),
  },
  {
    key: "discountPercent",
    header: "Discount",
    render: (row) => formatDiscountDisplay(row.discountPercent),
  },
  { key: "adjustment", header: "Adjustment" },
  {
    key: "netAmount",
    header: "Net",
    render: (row) => formatCurrencyDisplay(row.netAmount, row.invoiceCurrency),
  },
  {
    key: "voidDate",
    header: "Void Date",
    render: (row) => formatDateDisplay(row.voidDate),
  },
  { key: "routingStatus", header: "Routing Status" },
];

export const invoiceExportColumns: CvpColumn<PaymentData>[] = [
  {
    key: "invoiceNumber",
    header: "Invoice",
    render: (row) => String(row.invoiceNumber ?? ""),
  },
  {
    key: "vendorNumber",
    header: "Vendor Number",
    render: (row) => String(row.vendorNumber ?? ""),
  },
  { key: "vendorName", header: "Supplier" },
  { key: "paymentMethod", header: "Payment Method" },
  {
    key: "dueDate",
    header: "Scheduled Payment Date",
    render: (row) => String(formatDateDisplay(row.dueDate) ?? ""),
  },
  {
    key: "checkDate",
    header: "Settlement Date",
    render: (row) => String(formatDateDisplay(row.checkDate) ?? ""),
  },
  { key: "remainingDays", header: "Remaining Days" },
  {
    key: "amountDue",
    header: "Amount Due",
    render: (row) =>
      String(formatCurrencyDisplay(row.amountDue, row.invoiceCurrency) ?? ""),
  },
  { key: "paymentStatus", header: "Status" },
  {
    key: "paymentDate",
    header: "Payment Date",
    render: (row) => String(formatDateDisplay(row.paymentDate) ?? ""),
  },
  {
    key: "paymentReferenceNumber",
    header: "Reference Number",
    render: (row) => String(row.paymentReferenceNumber ?? ""),
  },
  {
    key: "grossAmount",
    header: "Gross",
    render: (row) =>
      String(formatCurrencyDisplay(row.grossAmount, row.invoiceCurrency) ?? ""),
  },
  {
    key: "discountPercent",
    header: "Discount",
    render: (row) => String(formatDiscountDisplay(row.discountPercent) ?? ""),
  },
  { key: "adjustment", header: "Adjustment" },
  {
    key: "netAmount",
    header: "Net",
    render: (row) =>
      String(formatCurrencyDisplay(row.netAmount, row.invoiceCurrency) ?? ""),
  },
  {
    key: "voidDate",
    header: "Void Date",
    render: (row) => String(formatDateDisplay(row.voidDate) ?? ""),
  },
  { key: "routingStatus", header: "Routing Status" },
];

const getStoredVendor = (): string => {
  return localStorage.getItem("vendorId") || "";
};

const getInitialFilters = (
  defaultSupplierId?: string,
): PaymentStatusFilterState => {
  return {
    suppliers: defaultSupplierId ? [defaultSupplierId] : [],
    invoiceNumber: "",
    paymentDateRange: "Next 90 days",
    method: "",
    paymentStatus: "",
  };
};

/**
 * Pure helper to expand supplier array into payload items using a provided hierarchyMap
 */
const resolvePayloadWithMap = (
  selectedSuppliers: string[],
  hMap: Record<string, string[]>,
): string[] => {
  const cleaned = selectedSuppliers.filter((s) => s && s.trim() !== "");
  const activeSuppliers =
    cleaned.length > 0 ? cleaned : [getStoredVendor()].filter(Boolean);

  const payloadList: string[] = [];

  activeSuppliers.forEach((id) => {
    const subVendors = hMap[id];
    if (subVendors && subVendors.length > 0) {
      payloadList.push(...subVendors);
    } else {
      payloadList.push(id);
    }
  });

  return Array.from(new Set(payloadList)).slice(0, MAX_SUBVENDOR_PAYLOAD_LIMIT);
};

export const InvoicePaymentStatus: React.FC<InvoicePaymentStatusProps> = () => {
  const location = useLocation();
  const history = useHistory();
  const { roles = [] } = useAuth();

  const [supplierOptions, setSupplierOptions] = useState<SupplierOption[]>([]);
  const [hierarchyMap, setHierarchyMap] = useState<Record<string, string[]>>(
    {},
  );
  const [loadingSuppliers, setLoadingSuppliers] = useState<boolean>(true);
  const [filters, setFilters] = useState<PaymentStatusFilterState>(() =>
    getInitialFilters(),
  );
  const [loading, setLoading] = useState<boolean>(false);

  const [searchTerm, setSearchTerm] = useState<string>("");

  const [uiPage, setUiPage] = useState<number>(1);
  const [uiPageSize, setUiPageSize] = useState<number>(50);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const [currentApiPage, setCurrentApiPage] = useState<number>(1);
  const [pageCache, setPageCache] = useState<Map<number, PaymentData[]>>(
    new Map(),
  );
  const [hasMore, setHasMore] = useState<boolean>(true);

  const isAdmin = roles.includes("ROLE_VendorFinanceMerchAdmin");

  const calculateDateRange = (
    range: string,
    invoiceNumber?: string,
  ): { fromDate: string; toDate: string } => {
    if (invoiceNumber?.trim() || !range) {
      return { fromDate: "", toDate: "" };
    }

    const today = new Date();
    const formatDateLocal = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    let from = new Date(today);
    let to = new Date(today);

    switch (range) {
      case "Next day":
        from.setDate(today.getDate() + 1);
        to.setDate(today.getDate() + 1);
        break;

      case "Next 7 days":
        to.setDate(today.getDate() + 6);
        break;
      case "Next 14 days":
        to.setDate(today.getDate() + 13);
        break;
      case "Next 31 days":
        to.setDate(today.getDate() + 30);
        break;
      case "Next 90 days":
        to.setDate(today.getDate() + 89);
        break;

      case "Today":
        break;

      case "Yesterday":
        from.setDate(today.getDate() - 1);
        to.setDate(today.getDate() - 1);
        break;
      case "Last 7 days":
        from.setDate(today.getDate() - 6);
        break;
      case "Last 14 days":
        from.setDate(today.getDate() - 13);
        break;
      case "Last 31 days":
        from.setDate(today.getDate() - 30);
        break;
      case "Last 90 days":
        from.setDate(today.getDate() - 89);
        break;
      case "Last 365 days":
        from.setDate(today.getDate() - 364);
        break;

      default:
        return { fromDate: "", toDate: "" };
    }

    return {
      fromDate: formatDateLocal(from),
      toDate: formatDateLocal(to),
    };
  };

  const resolvePayloadSuppliers = useCallback(
    (selectedSuppliers: string[]): string[] => {
      return resolvePayloadWithMap(selectedSuppliers, hierarchyMap);
    },
    [hierarchyMap],
  );

  const fetchApiPage = useCallback(
    async (
      apiPageNumber: number,
      appliedFilters: PaymentStatusFilterState,
      isJump: boolean = false,
      explicitHierarchyMap?: Record<string, string[]>,
    ) => {
      setLoading(true);
      try {
        const { fromDate, toDate } = calculateDateRange(
          appliedFilters.paymentDateRange,
          appliedFilters.invoiceNumber,
        );

        const supplierPayload = explicitHierarchyMap
          ? resolvePayloadWithMap(
              appliedFilters.suppliers || [],
              explicitHierarchyMap,
            )
          : resolvePayloadSuppliers(appliedFilters.suppliers || []);

        const payload = {
          searchCriteria: {
            supplier: supplierPayload,
            invoiceNumber: appliedFilters.invoiceNumber?.trim() || "",
            fromDate: fromDate,
            toDate: toDate,
            paymentMethod:
              appliedFilters.method === "" ? [""] : [appliedFilters.method],
            paymentStatus: appliedFilters.paymentStatus || "",
          },
          pagination: {
            page: apiPageNumber,
            pageSize: API_PAGE_SIZE,
          },
        };

        const response = await axios.post<PaymentResponse>(
          "/api/payment/status",
          payload,
        );

        const records = response.data?.data || [];
        const pagination = response.data?.pagination || { totalRecords: 0 };

        setPageCache((prevCache) => {
          if (isJump) {
            const freshMap = new Map<number, PaymentData[]>();
            freshMap.set(apiPageNumber, records);
            return freshMap;
          }

          const updated = new Map(prevCache);
          if (
            updated.size >= MAX_CACHED_API_PAGES &&
            !updated.has(apiPageNumber)
          ) {
            const oldestApiPage = updated.keys().next().value;
            if (oldestApiPage !== undefined) {
              updated.delete(oldestApiPage);
            }
          }

          updated.set(apiPageNumber, records);
          return updated;
        });

        setCurrentApiPage(apiPageNumber);
        setTotalRecords(pagination.totalRecords);

        if (records.length < API_PAGE_SIZE) {
          setHasMore(false);
        }
      } catch (error) {
        console.error(`Error fetching API page ${apiPageNumber}:`, error);
      } finally {
        setLoading(false);
      }
    },
    [resolvePayloadSuppliers],
  );

  const ensureRecordRangeLoaded = useCallback(
    async (targetUiPage: number, targetUiPageSize: number) => {
      const startRecordIndex = (targetUiPage - 1) * targetUiPageSize;
      const endRecordIndex = startRecordIndex + targetUiPageSize - 1;

      const startApiPage = Math.floor(startRecordIndex / API_PAGE_SIZE) + 1;
      const endApiPage = Math.floor(endRecordIndex / API_PAGE_SIZE) + 1;

      for (let apiPage = startApiPage; apiPage <= endApiPage; apiPage++) {
        if (!pageCache.has(apiPage)) {
          const isNonAdjacentJump = Math.abs(apiPage - currentApiPage) > 1;
          await fetchApiPage(apiPage, filters, isNonAdjacentJump);
        }
      }
    },
    [pageCache, filters, fetchApiPage, currentApiPage],
  );

  const handleFilterChange = <K extends keyof PaymentStatusFilterState>(
    key: K,
    value: PaymentStatusFilterState[K],
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handlePageChange = async (newPage: number) => {
    await ensureRecordRangeLoaded(newPage, uiPageSize);
    setUiPage(newPage);
  };

  const handlePageSizeChange = async (newPageSize: number) => {
    setUiPageSize(newPageSize);
    setUiPage(1);
    await ensureRecordRangeLoaded(1, newPageSize);
  };

  const handleApply = () => {
    setPageCache(new Map());
    setHasMore(true);
    setUiPage(1);
    setCurrentApiPage(1);
    setSearchTerm("");
    fetchApiPage(1, filters, true);
  };

  const handleReset = () => {
    const defaultSupplier = !isAdmin ? getStoredVendor() : "";
    const initial = getInitialFilters(defaultSupplier);
    setFilters(initial);
    setPageCache(new Map());
    setHasMore(true);
    setUiPage(1);
    setCurrentApiPage(1);
    setSearchTerm("");
    fetchApiPage(1, initial, true);
  };

  useEffect(() => {
    const loadSuppliers = async () => {
      setLoadingSuppliers(true);
      try {
        const result = isAdmin
          ? await fetchOnBoardVendors()
          : await fetchOnBoardVendorDetails();

        setSupplierOptions(result.options);
        setHierarchyMap(result.hierarchyMap);

        // Pre-select stored vendor ID for non-admin users
        const stored = getStoredVendor();
        const initialSupplier = !isAdmin && stored ? stored : "";
        const initialFilters = getInitialFilters(initialSupplier);

        setFilters(initialFilters);

        // Fetch first page using loaded hierarchyMap directly
        fetchApiPage(1, initialFilters, true, result.hierarchyMap);
      } catch (error) {
        console.error("Error fetching supplier options:", error);
      } finally {
        setLoadingSuppliers(false);
      }
    };

    loadSuppliers();
  }, [isAdmin]);

  const cachedRecords = useMemo(() => {
    const records: PaymentData[] = [];
    pageCache.forEach((pageRecords) => {
      records.push(...pageRecords);
    });
    return records;
  }, [pageCache]);

  const displayedRecords = useMemo(() => {
    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase().trim();
      const filtered = cachedRecords.filter((record) => {
        return invoiceColumns.some((col) => {
          let val: React.ReactNode = "";
          if (col.render) {
            val = col.render(record);
          } else if (col.key in record) {
            val = record[col.key as keyof PaymentData];
          }

          if (val === null || val === undefined) return false;
          return String(val).toLowerCase().includes(lowerSearch);
        });
      });
      const startIdx = (uiPage - 1) * uiPageSize;
      return filtered.slice(startIdx, startIdx + uiPageSize);
    }

    const startRecordIndex = (uiPage - 1) * uiPageSize;
    const endRecordIndex = startRecordIndex + uiPageSize;

    const items: PaymentData[] = [];
    for (let idx = startRecordIndex; idx < endRecordIndex; idx++) {
      const apiPageNumber = Math.floor(idx / API_PAGE_SIZE) + 1;
      const offsetInApiPage = idx % API_PAGE_SIZE;
      const apiPageData = pageCache.get(apiPageNumber);

      if (apiPageData && apiPageData[offsetInApiPage]) {
        items.push(apiPageData[offsetInApiPage]);
      }
    }

    return items;
  }, [cachedRecords, pageCache, uiPage, uiPageSize, searchTerm]);

  const getExportButtonStyle = (disabled: boolean): React.CSSProperties => ({
    backgroundColor: disabled ? "#a5d6a7" : "#168a58",
    color: "#ffffff",
    border: "none",
    borderRadius: "4px",
    padding: "7px 16px",
    fontSize: "13px",
    fontWeight: "bold",
    cursor: disabled ? "not-allowed" : "pointer",
    boxShadow: disabled ? "none" : "0 2px 4px rgba(0, 0, 0, 0.15)",
    textDecoration: "none",
    display: "inline-block",
  });

  const getFormattedDate = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}_${hours}${minutes}`;
  };

  const reportHeaders = useMemo(() => {
    return invoiceExportColumns.map((col) => ({
      label: col.header,
      key: col.key,
    }));
  }, []);

  const exportData = useMemo(() => {
    return cachedRecords.map((record) => {
      const rowObj: Record<string, any> = {};

      invoiceExportColumns.forEach((col) => {
        rowObj[col.key] = col.render
          ? col.render(record)
          : (record[col.key as keyof PaymentData] ?? "");
      });

      return rowObj;
    });
  }, [cachedRecords]);

  return (
    <div
      id="invoicePaymentStatus"
      className="container"
      style={{ maxWidth: "1850px", maxHeight: "2600px" }}
    >
      <div id="invoicePaymentStatusIntro" className="intro">
        <div
          id="invoicePaymentStatusHeader"
          className="section-heading mb-4"
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingTop: "20px",
            minHeight: "50px",
          }}
        >
          <span
            className="section-heading-lower"
            style={{
              fontSize: "1.9rem",
              color: "#6b3a06",
              textAlign: "center",
            }}
          >
            Payment Status
          </span>

          {/** Search hidden
          <div style={{ position: "absolute", right: 0 }}>
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setUiPage(1);
              }}
              style={{
                padding: "8px 12px",
                fontSize: "14px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                minWidth: "260px",
                boxSizing: "border-box",
                boxShadow: "inset 0 1px 2px rgba(0,0,0,0.075)",
              }}
            />
          </div>
          */}
        </div>

        <PaymentStatusFilterToolbar
          filters={filters}
          supplierOptions={supplierOptions}
          hierarchyMap={hierarchyMap}
          isLoadingSuppliers={loadingSuppliers}
          onFilterChange={handleFilterChange}
          onApply={handleApply}
          onReset={handleReset}
        />

        <div
          id="paymentsTableHeader"
          style={{
            display: "flex",
            gap: "8px",
            width: "100%",
            justifyContent: "flex-end",
            marginBottom: "10px",
          }}
        >
          <Report
            id="exportInvoicePaymentsReport"
            title="Export Payment Status"
            filename={`Payment_Status_${getFormattedDate()}.csv`}
            data={exportData}
            headers={reportHeaders}
          >
            <button
              id="exportInvoicePayments"
              style={getExportButtonStyle(displayedRecords.length === 0)}
              disabled={displayedRecords.length === 0}
            >
              Export
            </button>
          </Report>
        </div>

        <CvpTable
          data={displayedRecords}
          columns={invoiceColumns}
          loading={loading}
          page={uiPage}
          pageSize={uiPageSize}
          totalRecords={totalRecords}
          hasMore={hasMore}
          pageSizeOptions={[50, 75, 100, 500]}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
      <div style={{ height: "485px" }} className="row mb-5"></div>
    </div>
  );
};

export default InvoicePaymentStatus;
